import { afterNextRender, Component, computed, effect, inject, Injector, input, output, signal } from '@angular/core';

import { centralPageAnimation } from '../../../../../infrastructure/animations/central-page-animations';
import { showAnimation } from 'src/app/infrastructure/animations/show-animation';
import { _TabPageService } from '../../services/_tab-page.service';
import { CommonModule } from '@angular/common';
import { _CentralPageService } from '../../services/_central-page.service';
import { AnimationService } from 'src/app/infrastructure/services/loading-services/animation.service';
import { SectionDetailsComponent } from '../section-details/section-details.component';
import { ImageBoxDetailsComponent } from '../../../image-boxes/image-box-details/image-box-details.component';
import { LogService } from 'src/app/infrastructure/services/log.service';
import { LoadingPageService } from 'src/app/infrastructure/services/loading-services/loading-page.service';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';
import { _FormModelService } from 'src/app/infrastructure/services/model-services/_form-model.service';
import { _SectionBaseComponent } from 'src/app/infrastructure/components/section-components/_section-base-component';
import { pageMode } from 'src/app/infrastructure/models/page-mode';
import { sectionLayout } from 'src/app/infrastructure/models/section-layout';
import { motionDuration_read } from 'src/app/infrastructure/animations/motion-duration';

@Component({
    selector: 'app-section-details-tab-layout',
    templateUrl: './section-details-tab-layout.component.html',
    styleUrls: ['./section-details-tab-layout.component.scss'],
    standalone: true,
    host: {
        '[class.photo]': 'photo_show$$()',
        '[class.no-photo]': '!photo_show$$()',
        '[class.no-list]': '_isContentLayout$$()',
        '[class.edit-mode]': '_editMode$$()',
        '[class.transitions]': '_transitions$$()',
    },
    imports: [
        CommonModule,
        ImageBoxDetailsComponent,
        SectionDetailsComponent,
    ],
    animations: [
        centralPageAnimation,
        showAnimation
    ],
})
export class SectionDetailsTabLayoutComponent
{
    private readonly __animationService = injectRequired(AnimationService);
    private readonly __centralPageService = injectRequired(_CentralPageService);
    private readonly __tabPageService = inject(_TabPageService, {optional: true});
    private readonly __sectionComponent = inject(_SectionBaseComponent, {optional: true});
    
    out_btnDeleteEmit = output({alias: 'onDelete'});
    out_btnEditEmit = output({alias: 'onEdit'});
    out_btnCancelEmit = output({alias: 'onCancel'});
    out_btnSaveEmit = output({alias: 'onSave'});

    photo_size$$ = computed(() => this.pageMode$$() === 'tab-page' ? 'small' : 'large');
    photo_show$$ = computed(() => this.__tabPageService?.photoShow$$() ?? false);
    photo_guid$$ = computed(() => this.__tabPageService?.photoGuid$$() ?? null);
    photo_version$$ = computed(() => this.__tabPageService?.photoVersion$$() ?? null);
    photoDropEnabled$$ = computed(() => this.__tabPageService?.photoDropEnabled$$() ?? false);
    photoDragActive$$ = computed(() => this.__tabPageService?.photoDragActive$$() ?? false);
    photoCanUpload$$ = computed(() => this.__tabPageService?.photoCanUpload$$() ?? false);

    sheetWidth$$ = computed(() => this.pageMode$$() === 'tab-page' ? 'wide' : 'small');
    sectionDetails_show$$ = computed(() => this.pageMode$$() !== 'tab-page');
    sectionCompact_show$$ = computed(() => this.pageMode$$() === 'tab-page' || this.pageMode$$() === 'list-page');
    isListPage$$ = computed(() => this.pageLayout$$() == 'list-page');
    
    pageLayout$$ = this.__centralPageService.pageLayout$$;
    pageMode$$ = this.__centralPageService.pageMode$$;
    // Niet klikbaar buiten details/medium, bij een read-only box, of als er een foto staat maar je
    // niet mag doorklikken naar ImageDetails (BtnImageDetails_Show, zie _TabPageService.photoCanOpen$$). Een
    // lege box valt onder het upload-recht (image-box-details, canUpload).
    noClick$$ = computed(() => (this.pageMode$$() != pageMode.detailsPage && this.pageMode$$() != pageMode.mediumPage)
        || (this.__tabPageService?.photoReadOnly$$() ?? false)
        || (!!this.photo_guid$$() && !(this.__tabPageService?.photoCanOpen$$() ?? false)));
    sectionCompact_template$$ = computed(() => this.__tabPageService?.sectionCompact_template$$() ?? null);

    // Paginawissel-duur = gedeelde animatieduur van het thema (--layout-motion-duration, zie motion-duration.ts),
    // gelezen bij elke standwissel zodat een thema-wijziging direct meetelt.
    protected _motionDuration$$ = computed(() => { this.pageMode$$(); return motionDuration_read(); });
    protected _showHideEnterTime$$ = this._motionDuration$$;
    protected _showHideLeaveTime$$ = this._motionDuration$$;

    protected _isContentLayout$$ = computed(() =>
        (this.__tabPageService?.sectionLayout$$() ?? sectionLayout.form) === sectionLayout.content
    );
    protected _editMode$$ = computed(() => this.pageMode$$() === pageMode.editPage || this.pageMode$$() === pageMode.createPage);

    // CSS-transities (controls-box padding bij .no-list) pas aan zodra de pagina geladen en getekend is: bij openen
    // wisselt de sectie-layout van form (default) naar content (bv. ThemeDetails) — zonder deze poort animeerde de
    // rechter-inzet dan zichtbaar bij het openen. Een pagina hoort direct in positie te openen.
    private readonly __loadingPageService = injectRequired(LoadingPageService);
    private readonly __injector = inject(Injector);
    protected readonly _transitions$$ = signal(false);
    private readonly __transitions_enable = effect(() => {
        if (this._transitions$$() || this.__loadingPageService.isLoading$$()) return;
        afterNextRender(() => requestAnimationFrame(() => this._transitions$$.set(true)), { injector: this.__injector });
    });
    protected _detailsEnterTime$$ = computed(() => this._isContentLayout$$() ? '0ms' : this._showHideEnterTime$$());
    protected _detailsLeaveTime$$ = computed(() => this._isContentLayout$$() ? '0ms' : this._showHideLeaveTime$$());

    constructor() {
    }

    // Bestand gekozen/gemaakt via de hoofdfoto-box (camera/galerij): doorgeven aan de parent, die
    // dezelfde upload-procedure als de fotolijst draait en het resultaat als hoofdfoto koppelt.
    emit_photoUpload(file: File): void {
        this.__tabPageService?.photoUpload_emit(file);
    }

    // Klik op de hoofdfoto-box: open ImageDetailsComponent (geen click als er nog geen foto is —
    // dan toont de box de upload-actions in plaats van een foto om op te klikken).
    emit_photoClick(): void {
        this.__tabPageService?.photo_navigate();
    }

    emit_animation_start(): void {
        this.__animationService.add();
    }

    emit_animation_end(): void {
        this.__animationService.remove();
    }

    emit_btnCancel(): void {
        this.out_btnCancelEmit.emit();
    }

    emit_btnDelete(): void {
        this.out_btnDeleteEmit.emit();
    }

    emit_btnEdit(): void {
        this.out_btnEditEmit.emit();
    }

    emit_btnSave(): void {
        this.out_btnSaveEmit.emit();
    }

    ngAfterViewInit(): void {
        if(!this.sectionCompact_template$$() && this.pageMode$$() === 'tab-page') {
            LogService.envDev.error('template compact section missing!!');
        }
    }
}

